import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, PLATFORM_ID } from '@angular/core';
import { NeighborhoodStats } from '../../../core/models/domain.models';
import { fmtEurPerM2, fmtPct } from '../../../core/i18n/labels';
import { ThemeService } from '../../../core/services/theme.service';

interface MapPoint {
  neighborhoodId: number;
  name: string;
  x: number;
  y: number;
  r: number;
  opacity: number;
  label: string;
}

const SIZE = 300;
const PAD = SIZE * 0.16;

/** Stylized bubble map of a city's neighborhoods, positioned by real lat/lng (no basemap tiles). */
@Component({
  selector: 'app-neighborhood-map',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './neighborhood-map.component.html',
  styleUrl: './neighborhood-map.component.scss',
})
export class NeighborhoodMapComponent {
  private readonly theme = inject(ThemeService);
  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  readonly viewBox = `0 0 ${SIZE} ${SIZE}`;

  data = input.required<NeighborhoodStats[]>();
  /** Which value drives bubble size/opacity. */
  metric = input<'price' | 'yoy'>('price');
  height = input<number>(320);
  ariaLabel = input<string>('Карта на кварталите');

  get color(): string {
    return this.theme.theme() === 'dark' ? '#3FB8AE' : '#0F8B84';
  }

  points = computed<MapPoint[]>(() => {
    // Only geocoded neighborhoods can be plotted. A missing coordinate read as
    // 0 would put the marker off the coast of Africa and stretch the map's
    // bounds so every real neighborhood collapsed into a corner.
    const rows = this.data().filter(
      (n): n is typeof n & { lat: number; lng: number } => n.lat !== null && n.lng !== null,
    );
    if (!rows.length) return [];

    const latRad = (average(rows.map((n) => n.lat)) * Math.PI) / 180;
    const cosLat = Math.cos(latRad) || 1;
    const projected = rows.map((n) => ({ x: n.lng * cosLat, y: -n.lat }));

    const xs = projected.map((p) => p.x);
    const ys = projected.map((p) => p.y);
    const spanX = Math.max(...xs) - Math.min(...xs) || 1e-6;
    const spanY = Math.max(...ys) - Math.min(...ys) || 1e-6;
    const scale = (SIZE - 2 * PAD) / Math.max(spanX, spanY);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);

    const metric = this.metric();
    const values = rows.map((n) => (metric === 'yoy' ? (n.yoyPct ?? 0) : n.medianSaleEurPerM2));
    const minV = Math.min(...values);
    const maxV = Math.max(...values);

    return rows.map((n, i) => {
      const t = maxV === minV ? 0.5 : (values[i] - minV) / (maxV - minV);
      return {
        neighborhoodId: n.neighborhoodId,
        name: n.name,
        x: PAD + (projected[i].x - minX) * scale,
        y: PAD + (projected[i].y - minY) * scale,
        r: 9 + t * 20,
        opacity: 0.3 + t * 0.55,
        label: metric === 'yoy' ? fmtPct(n.yoyPct) : fmtEurPerM2(n.medianSaleEurPerM2),
      };
    });
  });
}

function average(values: number[]): number {
  return values.reduce((a, b) => a + b, 0) / values.length;
}
